import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import TrendElevated from '@/assets/icons/trend_elevated.svg?component';
import TrendNormal from '@/assets/icons/trend_normal.svg?component';
import MetricCard, {
    type MetricCardBadge,
    type MetricCardIcon,
    type MetricTone,
} from '@/components/molecules/MetricCard/MetricCard';
import type { BehaviorTrendType } from '@/routes/Client/Cage/pages/CageBehavior/BehaviorDashboard/types';

interface BehaviorMetricCardProps {
    title: string;
    value: string | number | ReactNode;
    isLoading?: boolean;
    icon: MetricCardIcon;
    tone: MetricTone;
    className?: string;
    iconCustomSize?: number;
    trendType?: BehaviorTrendType;
    suffix?: string;
}

/**
 * Maps a behaviour verdict to the generic card's badge.
 *
 * The thresholds that produce `trendType` are behaviour-specific and so is the
 * copy, which is why this mapping stays in the module instead of moving into
 * `MetricCard`.
 */
function resolveTrendBadge(
    trendType: BehaviorTrendType,
    t: TFunction<'behaviorModule'>
): MetricCardBadge | undefined {
    switch (trendType) {
        case 'elevated':
            return {
                icon: TrendElevated,
                label: t('overview.elevated'),
                tone: 'danger',
            };
        case 'reduced':
            return {
                icon: TrendElevated,
                label: t('overview.reduced'),
                tone: 'danger',
            };
        case 'normal':
            return {
                icon: TrendNormal,
                label: t('overview.normal'),
                tone: 'success',
            };
        case 'optimal':
            return {
                icon: TrendNormal,
                label: t('overview.optimal'),
                tone: 'success',
            };
        case 'suboptimal':
            return {
                icon: TrendNormal,
                label: t('overview.suboptimal'),
                tone: 'caution',
            };
        case 'stressInducing':
            return {
                icon: TrendNormal,
                label: t('overview.stressInducing'),
                tone: 'warning',
            };
        case 'seriousImpairment':
            return {
                icon: TrendNormal,
                label: t('overview.seriousImpairment'),
                tone: 'danger',
            };
        case 'low':
            return {
                icon: TrendNormal,
                label: t('overview.low'),
                tone: 'danger',
            };
        default:
            return undefined;
    }
}

/**
 * Behaviour-flavoured `MetricCard`: same layout, plus the trend pill driven by
 * `BehaviorTrendType`.
 */
function BehaviorMetricCard(props: BehaviorMetricCardProps) {
    const { trendType = 'none', ...metricCardProps } = props;
    const { t } = useTranslation('behaviorModule');

    return <MetricCard {...metricCardProps} badge={resolveTrendBadge(trendType, t)} />;
}

export default BehaviorMetricCard;
